import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 应用安全指南`,
            description: category.description,
            url: `https://example.com/category/${category.slug}`,
            inLanguage: 'zh-Hans',
            isPartOf: {
              '@type': 'WebSite',
              name: '应用安全指南',
              url: 'https://example.com/',
            },
            about: {
              '@type': 'Thing',
              name: category.name,
              description: category.description,
            },
          }),
        }}
      />

      <Navbar />
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="py-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  {category.description}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-foreground-600 border border-background-200/70 hover:border-primary-200 hover:text-primary-500'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-file-list-3-line text-5xl text-foreground-300 block mb-4"></i>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-white rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{article.date}</span>
                        <span className="text-xs text-foreground-400">·</span>
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center flex-wrap gap-1 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-background-100 text-foreground-500 rounded text-xs whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}